// SEO Configuration for TAK Business Manager
export const seoConfig = {
  // Basic SEO Data
  siteName: "TAK Business Manager",
  siteUrl: "https://takbusinessmanager.com",
  defaultTitle:
    "TAK Business Manager - Mobile App for Small Business Management",
  defaultDescription:
    "Transform your business with TAK Business Manager mobile app. Manage sales, inventory, customers, and analytics on the go. Free download for Android, iOS coming soon. Boost efficiency by 40%.",
  defaultKeywords: [
    "business management app",
    "mobile POS system",
    "inventory management",
    "sales tracking",
    "small business app",
    "business analytics",
    "TAK Business Manager",
    "point of sale app",
    "business productivity",
    "retail management",
    "mobile business software",
    "entrepreneur tools",
    "business dashboard",
    "sales reporting",
    "customer management",
  ],

  // Social Media
  social: {
    twitter: "@TAKKinship",
    facebook: "https://www.facebook.com/share/17WkQTwpwR/",
    linkedin: "https://linkedin.com/company/takkisnhip",
    instagram: "https://www.instagram.com/tak_business_manager",
  },

  // Company Info
  company: {
    name: "TAK Kinship Technologies",
    email: "support@takbusiness.com",
    phone: "+256783808236",
    address: "Kampala, Uganda",
  },

  // App Store Info
  app: {
    name: "TAK Business Manager",
    package: "com.tak.business_pos",
    category: "Business",
    rating: "4.8",
    reviews: "127",
    price: "Free",
    android: {
      url: "https://play.google.com/store/apps/details?id=com.tak.business_pos",
      available: true,
    },
    ios: {
      url: "#",
      available: false,
      comingSoon: true,
    },
  },

  // Images for SEO
  images: {
    logo: "/assets/logos/App_Icon.png",
    ogImage: "/assets/images/og-image.png",
    twitterImage: "/assets/images/twitter-image.png",
    screenshots: [
      "/assets/images/mockup-1.png",
      "/assets/images/mockup-2.png",
      "/assets/images/mockup-3.png",
      "/assets/images/mockup-4.png",
      "/assets/images/mockup-5.png",
    ],
  },

  // Structured Data Templates
  structuredData: {
    organization: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "TAK Kinship Technologies",
      url: "https://takkinship.com",
      logo: "https://takbusinessmanager.com/assets/logos/App_Icon.png",
      sameAs: [
        "https://www.facebook.com/share/17WkQTwpwR/",
        "https://twitter.com/takkinship",
        "https://linkedin.com/company/takkinship",
        "https://www.instagram.com/tak_business_manager",
      ],
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+256783808236",
        contactType: "customer service",
        email: "support@takbusiness.com",
      },
    },

    mobileApp: {
      "@context": "https://schema.org",
      "@type": "MobileApplication",
      name: "TAK Business Manager",
      description:
        "Mobile app for small business management including sales tracking, inventory management, and business analytics",
      url: "https://takbusinessmanager.com",
      applicationCategory: "BusinessApplication",
      operatingSystem: ["Android 6.0", "iOS 12.0"],
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
      author: {
        "@type": "Organization",
        name: "TAK Kinship Technologies",
        url: "https://takkinship.com",
      },
      downloadUrl:
        "https://play.google.com/store/apps/details?id=com.tak.business_pos",
      screenshot: [
        "https://takbusinessmanager.com/assets/images/mockup-1.png",
        "https://takbusinessmanager.com/assets/images/mockup-2.png",
        "https://takbusinessmanager.com/assets/images/mockup-3.png",
      ],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.8",
        ratingCount: "127",
      },
      featureList: [
        "Point of Sale (POS) System",
        "Inventory Management",
        "Sales Reporting and Analytics",
        "Customer Management",
        "Staff Management",
        "Multi-location Support",
        "Receipt Printing via Bluetooth",
        "Real-time Notifications",
        "Offline Functionality",
        "Cloud Synchronization",
      ],
    },

    website: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "TAK Business Manager",
      url: "https://takbusinessmanager.com",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://takbusinessmanager.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
  },

  // Page-specific SEO
  pages: {
    home: {
      title:
        "TAK Business Manager - Mobile App for Small Business Management | TAK Kinship Technologies",
      description:
        "Transform your business with TAK Business Manager mobile app. Manage sales, inventory, customers, and analytics on the go. Free download for Android, iOS coming soon. Boost efficiency by 40%.",
      keywords:
        "business management app, mobile POS system, inventory management, sales tracking, small business app",
    },
  },
};

// SEO Helper Functions
export const generateMetaTags = (pageConfig) => {
  const config = { ...seoConfig, ...pageConfig };

  return {
    title: config.title || seoConfig.defaultTitle,
    description: config.description || seoConfig.defaultDescription,
    keywords: config.keywords
      ? Array.isArray(config.keywords)
        ? config.keywords.join(", ")
        : config.keywords
      : seoConfig.defaultKeywords.join(", "),
    ogTitle: config.title || seoConfig.defaultTitle,
    ogDescription: config.description || seoConfig.defaultDescription,
    ogImage: config.ogImage || seoConfig.images.ogImage,
    twitterTitle: config.title || seoConfig.defaultTitle,
    twitterDescription: config.description || seoConfig.defaultDescription,
    twitterImage: config.twitterImage || seoConfig.images.twitterImage,
    canonical: config.canonical || seoConfig.siteUrl,
  };
};

export const injectStructuredData = (data) => {
  try {
    if (!data || typeof data !== "object") {
      console.warn("Invalid structured data provided:", data);
      return;
    }

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
  } catch (error) {
    console.warn("Error injecting structured data:", error);
  }
};

export const updatePageSEO = (pageConfig) => {
  try {
    const meta = generateMetaTags(pageConfig);

    // Update title
    document.title = meta.title;

    // Update meta tags
    const updateMeta = (name, content) => {
      try {
        let element = document.querySelector(`meta[name="${name}"]`);
        if (!element) {
          element = document.createElement("meta");
          element.name = name;
          document.head.appendChild(element);
        }
        element.content = content;
      } catch (error) {
        console.warn(`Error updating meta tag ${name}:`, error);
      }
    };

    const updateProperty = (property, content) => {
      try {
        let element = document.querySelector(`meta[property="${property}"]`);
        if (!element) {
          element = document.createElement("meta");
          element.setAttribute("property", property);
          document.head.appendChild(element);
        }
        element.content = content;
      } catch (error) {
        console.warn(`Error updating property ${property}:`, error);
      }
    };

    // Update basic meta tags
    updateMeta("description", meta.description);
    updateMeta("keywords", meta.keywords);
    updateMeta("author", seoConfig.company.name);

    // Update Open Graph tags
    updateProperty("og:title", meta.ogTitle);
    updateProperty("og:description", meta.ogDescription);
    updateProperty("og:image", meta.ogImage);
    updateProperty("og:url", meta.canonical);

    // Update Twitter tags
    updateProperty("twitter:title", meta.twitterTitle);
    updateProperty("twitter:description", meta.twitterDescription);
    updateProperty("twitter:image", meta.twitterImage);

    // Update canonical URL
    try {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.rel = "canonical";
        document.head.appendChild(canonical);
      }
      canonical.href = meta.canonical;
    } catch (error) {
      console.warn("Error updating canonical URL:", error);
    }
  } catch (error) {
    console.warn("Error updating page SEO:", error);
  }
};

export default seoConfig;
